import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Archive, Loader2, RotateCcw, Trash2 } from "lucide-react";
import type { Manual } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useShowError } from "../../error/ErrorProvider.js";
import { DeleteManualDialog } from "./DeleteManualDialog.js";
import { ManualReferenceElement } from "./ManualReferenceElement.js";
import { useFacilityManuals, useFacilityManualTrash } from "./useFacilityManuals.js";
import { useRestoreManual, useUnarchiveManual } from "./useManualMutations.js";

export type ManualBinKind = "archived" | "trash";

const DAY_MS = 86_400_000;

/**
 * A facility's archived manuals or its trash (ADR-056), opened from the area-info drawer. Both show manual-reference-elements
 * without "Öffnen", with the same two actions: restore, and delete completely through the type-to-confirm dialog. Without the
 * manuals capability the archived list is read-only (the trash isn't reachable then; the server decides either way).
 */
export function ManualBinDialog({
  kind,
  facilityId,
  facilityName,
  canManage,
  session,
  onClose,
}: {
  kind: ManualBinKind;
  facilityId: number;
  facilityName: string;
  canManage: boolean;
  session: AuthSession | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const showError = useShowError();
  const archived = useFacilityManuals(session, kind === "archived" ? facilityId : null, true);
  const trash = useFacilityManualTrash(session, kind === "trash" ? facilityId : null);
  const unarchive = useUnarchiveManual(session);
  const restoreFromTrash = useRestoreManual(session);
  const restore = kind === "trash" ? restoreFromTrash : unarchive;
  const [deleting, setDeleting] = useState<{ id: number; name: string } | null>(null);
  const [announce, setAnnounce] = useState("");

  const current = kind === "trash" ? trash : archived;
  const manuals: Array<Manual & { purgeAt?: string; archived?: boolean; parentTrashed?: boolean }> = (kind === "trash" ? trash.data?.manuals : archived.data) ?? [];

  const daysLeft = (purgeAt: string) => Math.max(0, Math.ceil((Date.parse(purgeAt) - Date.now()) / DAY_MS));
  const onRestore = (m: Manual) =>
    restore.mutate(
      { id: m.id, expectedFrom: m.state },
      { onSuccess: () => setAnnounce(t("manualManagement.bin.restored", { name: m.name })), onError: showError },
    );

  const actions = (m: Manual & { parentTrashed?: boolean }): ReactNode =>
    canManage ? (
      <>
        <Button
          variant="outline"
          className="h-[38px] flex-1 rounded-[10px] px-3 text-[13px] font-semibold @md:flex-none"
          aria-label={t("manualManagement.bin.restoreNamed", { name: m.name })}
          isDisabled={restore.isPending || m.parentTrashed === true}
          onPress={() => onRestore(m)}
        >
          <RotateCcw aria-hidden="true" />
          {t("other.restoreButton")}
        </Button>
        <Button
          variant="destructive"
          className="h-[38px] flex-1 rounded-[10px] px-3 text-[13px] font-semibold @md:flex-none"
          aria-label={t("manualManagement.bin.deleteNamed", { name: m.name })}
          onPress={() => setDeleting({ id: m.id, name: m.name })}
        >
          <Trash2 aria-hidden="true" />
          {t("manualManagement.delete.confirm")}
        </Button>
      </>
    ) : null;

  return (
    <Dialog isOpen isDismissable onOpenChange={(open) => !open && onClose()} size="xl">
      <DialogTitle>{kind === "trash" ? t("dialogWindows.binWindowTitle") : t("manualManagement.bin.archivedTitle")}</DialogTitle>
      <DialogDescription>
        {kind === "trash" && trash.data
          ? t("manualManagement.bin.trashInfo", { name: facilityName, days: trash.data.retentionDays })
          : t("manualManagement.bin.archivedInfo", { name: facilityName })}
      </DialogDescription>
      <span role="status" className="sr-only">
        {announce}
      </span>

      {current.isPending ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("common.loading")}
        </p>
      ) : current.isError ? (
        <div className="text-muted-foreground flex flex-col items-start gap-2 text-sm">
          <p className="m-0">{t("manualManagement.list.loadFailed")}</p>
          <Button variant="outline" size="sm" onPress={() => void current.refetch()} isDisabled={current.isFetching}>
            {t("common.tryAgain")}
          </Button>
        </div>
      ) : manuals.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {kind === "trash" ? t("manualSelection.sidebar.noManualsInBin") : t("manualManagement.list.noArchived")}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {manuals.map((m) => (
            <li key={m.id}>
              <ManualReferenceElement
                manual={{ ...m, facilityId }}
                path={m.categoryName}
                note={m.parentTrashed ? t("manualManagement.bin.originalInTrash") : undefined}
                badge={
                  m.purgeAt ? (
                    <>
                      {m.archived ? (
                        <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium">
                          <Archive aria-hidden="true" className="size-3" />
                          {t("common.archived")}
                        </span>
                      ) : null}
                      <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[0.7rem] font-medium">
                        {t("manualManagement.bin.daysLeft", { count: daysLeft(m.purgeAt) })}
                      </span>
                    </>
                  ) : null
                }
                actions={actions(m)}
              />
            </li>
          ))}
        </ul>
      )}

      <DialogFooter>
        <Button onPress={onClose}>{t("manualManagement.misc.close")}</Button>
      </DialogFooter>

      {deleting ? (
        <DeleteManualDialog
          manual={deleting}
          session={session}
          onClose={() => setDeleting(null)}
          onDeleted={(name) => setAnnounce(t("manualManagement.announce.deleted", { name }))}
        />
      ) : null}
    </Dialog>
  );
}
